import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

const Settings: React.FC = () => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<'general' | 'notifications' | 'security' | 'api'>('general');

  // Mock settings state
  const [settings, setSettings] = useState({
    siteName: 'DARTIQON',
    siteDescription: 'Automatikus Darts Scoring & Versenyplatform',
    maintenanceMode: false,
    allowRegistration: true,
    requireEmailVerification: true,
    enableNotifications: true,
    emailNotifications: true,
    pushNotifications: false,
    twoFactorAuth: false,
    sessionTimeout: 30,
    apiRateLimit: 1000,
  });

  const handleToggle = (key: keyof typeof settings) => {
    setSettings(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  return (
    <div className="space-y-6 pt-16 lg:pt-0 pb-16 sm:pb-0">
      {/* Header */}
      <div>
        <h1 className="text-4xl font-bold text-gradient-cyan mb-2">
          ⚙️ Beállítások
        </h1>
        <p className="text-[#B8B8C3]">
          Rendszer konfiguráció és beállítások kezelése
        </p>
      </div>

      {/* Tabs */}
      <div className="card-dark p-2">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          <button
            onClick={() => setActiveTab('general')}
            className={`py-3 px-2 sm:px-4 rounded-lg font-medium transition-all text-sm sm:text-base ${
              activeTab === 'general'
                ? 'bg-gradient-to-r from-[#00AFF2] to-[#2bc48a] text-white'
                : 'text-[#B8B8C3] hover:bg-[#1d232a]'
            }`}
          >
            🌐 Általános
          </button>
          <button
            onClick={() => setActiveTab('notifications')}
            className={`py-3 px-2 sm:px-4 rounded-lg font-medium transition-all text-sm sm:text-base ${
              activeTab === 'notifications'
                ? 'bg-gradient-to-r from-[#00AFF2] to-[#2bc48a] text-white'
                : 'text-[#B8B8C3] hover:bg-[#1d232a]'
            }`}
          >
            🔔 Értesítések
          </button>
          <button
            onClick={() => setActiveTab('security')}
            className={`py-3 px-2 sm:px-4 rounded-lg font-medium transition-all text-sm sm:text-base ${
              activeTab === 'security'
                ? 'bg-gradient-to-r from-[#00AFF2] to-[#2bc48a] text-white'
                : 'text-[#B8B8C3] hover:bg-[#1d232a]'
            }`}
          >
            🔒 Biztonság
          </button>
          <button
            onClick={() => setActiveTab('api')}
            className={`py-3 px-2 sm:px-4 rounded-lg font-medium transition-all text-sm sm:text-base ${
              activeTab === 'api'
                ? 'bg-gradient-to-r from-[#00AFF2] to-[#2bc48a] text-white'
                : 'text-[#B8B8C3] hover:bg-[#1d232a]'
            }`}
          >
            🔌 API
          </button>
        </div>
      </div>

      {/* General Settings */}
      {activeTab === 'general' && (
        <div className="space-y-6">
          <div className="card-dark p-6">
            <h2 className="text-2xl font-bold text-white mb-6">Általános Beállítások</h2>
            
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-[#B8B8C3] mb-2">
                  Oldal Neve
                </label>
                <input
                  type="text"
                  value={settings.siteName}
                  onChange={(e) => setSettings({...settings, siteName: e.target.value})}
                  className="input-dark w-full"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#B8B8C3] mb-2">
                  Oldal Leírása
                </label>
                <textarea
                  value={settings.siteDescription}
                  onChange={(e) => setSettings({...settings, siteDescription: e.target.value})}
                  className="input-dark w-full"
                  rows={3}
                />
              </div>

              <div className="h-px bg-gradient-to-r from-transparent via-[#00AFF2] to-transparent"></div>

              <div className="flex items-center justify-between p-4 bg-[#15191E] rounded-lg">
                <div>
                  <p className="text-white font-medium">Karbantartási Mód</p>
                  <p className="text-[#B8B8C3] text-sm">Oldal átmenetileg nem elérhető a felhasználók számára</p>
                </div>
                <button
                  onClick={() => handleToggle('maintenanceMode')}
                  className={`relative w-14 h-7 rounded-full transition-colors ${
                    settings.maintenanceMode ? 'bg-[#00AFF2]' : 'bg-[#31383D]'
                  }`}
                >
                  <div className={`absolute top-1 left-1 w-5 h-5 bg-white rounded-full transition-transform ${
                    settings.maintenanceMode ? 'transform translate-x-7' : ''
                  }`}></div>
                </button>
              </div>

              <div className="h-px bg-gradient-to-r from-transparent via-[#00AFF2] to-transparent"></div>

              <div className="flex items-center justify-between p-4 bg-[#15191E] rounded-lg">
                <div>
                  <p className="text-white font-medium">Regisztráció Engedélyezése</p>
                  <p className="text-[#B8B8C3] text-sm">Új felhasználók regisztrálhatnak</p>
                </div>
                <button
                  onClick={() => handleToggle('allowRegistration')}
                  className={`relative w-14 h-7 rounded-full transition-colors ${
                    settings.allowRegistration ? 'bg-[#00AFF2]' : 'bg-[#31383D]'
                  }`}
                >
                  <div className={`absolute top-1 left-1 w-5 h-5 bg-white rounded-full transition-transform ${
                    settings.allowRegistration ? 'transform translate-x-7' : ''
                  }`}></div>
                </button>
              </div>

              <div className="h-px bg-gradient-to-r from-transparent via-[#00AFF2] to-transparent"></div>

              <div className="flex items-center justify-between p-4 bg-[#15191E] rounded-lg">
                <div>
                  <p className="text-white font-medium">Email Megerősítés Kötelező</p>
                  <p className="text-[#B8B8C3] text-sm">Felhasználóknak meg kell erősíteniük email címüket</p>
                </div>
                <button
                  onClick={() => handleToggle('requireEmailVerification')}
                  className={`relative w-14 h-7 rounded-full transition-colors ${
                    settings.requireEmailVerification ? 'bg-[#00AFF2]' : 'bg-[#31383D]'
                  }`}
                >
                  <div className={`absolute top-1 left-1 w-5 h-5 bg-white rounded-full transition-transform ${
                    settings.requireEmailVerification ? 'transform translate-x-7' : ''
                  }`}></div>
                </button>
              </div>
            </div>

            <div className="mt-6 flex gap-4">
              <button className="btn-primary">
                💾 Változtatások Mentése
              </button>
              <button className="btn-secondary">
                ↺ Visszaállítás
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Notifications Settings */}
      {activeTab === 'notifications' && (
        <div className="card-dark p-6">
          <h2 className="text-2xl font-bold text-white mb-6">Értesítési Beállítások</h2>
          
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-[#15191E] rounded-lg">
              <div>
                <p className="text-white font-medium">Értesítések Engedélyezése</p>
                <p className="text-[#B8B8C3] text-sm">Rendszer értesítések be/kikapcsolása</p>
              </div>
              <button
                onClick={() => handleToggle('enableNotifications')}
                className={`relative w-14 h-7 rounded-full transition-colors ${
                  settings.enableNotifications ? 'bg-[#00AFF2]' : 'bg-[#31383D]'
                }`}
              >
                <div className={`absolute top-1 left-1 w-5 h-5 bg-white rounded-full transition-transform ${
                  settings.enableNotifications ? 'transform translate-x-7' : ''
                }`}></div>
              </button>
            </div>

            <div className="h-px bg-gradient-to-r from-transparent via-[#00AFF2] to-transparent"></div>

            <div className="flex items-center justify-between p-4 bg-[#15191E] rounded-lg">
              <div>
                <p className="text-white font-medium">Email Értesítések</p>
                <p className="text-[#B8B8C3] text-sm">Értesítések küldése emailben</p>
              </div>
              <button
                onClick={() => handleToggle('emailNotifications')}
                className={`relative w-14 h-7 rounded-full transition-colors ${
                  settings.emailNotifications ? 'bg-[#00AFF2]' : 'bg-[#31383D]'
                }`}
              >
                <div className={`absolute top-1 left-1 w-5 h-5 bg-white rounded-full transition-transform ${
                  settings.emailNotifications ? 'transform translate-x-7' : ''
                }`}></div>
              </button>
            </div>

            <div className="h-px bg-gradient-to-r from-transparent via-[#00AFF2] to-transparent"></div>

            <div className="flex items-center justify-between p-4 bg-[#15191E] rounded-lg">
              <div>
                <p className="text-white font-medium">Push Értesítések</p>
                <p className="text-[#B8B8C3] text-sm">Böngésző push értesítések</p>
              </div>
              <button
                onClick={() => handleToggle('pushNotifications')}
                className={`relative w-14 h-7 rounded-full transition-colors ${
                  settings.pushNotifications ? 'bg-[#00AFF2]' : 'bg-[#31383D]'
                }`}
              >
                <div className={`absolute top-1 left-1 w-5 h-5 bg-white rounded-full transition-transform ${
                  settings.pushNotifications ? 'transform translate-x-7' : ''
                }`}></div>
              </button>
            </div>
          </div>

          <div className="mt-6">
            <button className="btn-primary">
              💾 Változtatások Mentése
            </button>
          </div>
        </div>
      )}

      {/* Security Settings */}
      {activeTab === 'security' && (
        <div className="card-dark p-6">
          <h2 className="text-2xl font-bold text-white mb-6">Biztonsági Beállítások</h2>
          
          <div className="space-y-6">
            <div className="flex items-center justify-between p-4 bg-[#15191E] rounded-lg">
              <div>
                <p className="text-white font-medium">Kétfaktoros Hitelesítés</p>
                <p className="text-[#B8B8C3] text-sm">Extra biztonsági réteg bejelentkezéshez</p>
              </div>
              <button
                onClick={() => handleToggle('twoFactorAuth')}
                className={`relative w-14 h-7 rounded-full transition-colors ${
                  settings.twoFactorAuth ? 'bg-[#00AFF2]' : 'bg-[#31383D]'
                }`}
              >
                <div className={`absolute top-1 left-1 w-5 h-5 bg-white rounded-full transition-transform ${
                  settings.twoFactorAuth ? 'transform translate-x-7' : ''
                }`}></div>
              </button>
            </div>

            <div className="h-px bg-gradient-to-r from-transparent via-[#00AFF2] to-transparent"></div>

            <div>
              <label className="block text-sm font-medium text-[#B8B8C3] mb-2">
                Session Timeout (perc)
              </label>
              <input
                type="number"
                value={settings.sessionTimeout}
                onChange={(e) => setSettings({...settings, sessionTimeout: parseInt(e.target.value)})}
                className="input-dark w-full"
                min="5"
                max="120"
              />
              <p className="text-xs text-[#B8B8C3] mt-1">
                Automatikus kijelentkezés inaktivitás után
              </p>
            </div>

            <div className="p-4 bg-yellow-900/20 border border-yellow-500/50 rounded-lg">
              <p className="text-yellow-400 font-medium mb-2">⚠️ Biztonsági Figyelmeztetés</p>
              <p className="text-[#B8B8C3] text-sm">
                Győződj meg róla, hogy az SSL tanúsítvány érvényes és a HTTPS protokoll aktív.
              </p>
            </div>
          </div>

          <div className="mt-6">
            <button className="btn-primary">
              💾 Változtatások Mentése
            </button>
          </div>
        </div>
      )}

      {/* API Settings */}
      {activeTab === 'api' && (
        <div className="card-dark p-6">
          <h2 className="text-2xl font-bold text-white mb-6">API Beállítások</h2>
          
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-[#B8B8C3] mb-2">
                API Rate Limit (kérés/óra)
              </label>
              <input
                type="number"
                value={settings.apiRateLimit}
                onChange={(e) => setSettings({...settings, apiRateLimit: parseInt(e.target.value)})}
                className="input-dark w-full"
                min="100"
                max="10000"
              />
              <p className="text-xs text-[#B8B8C3] mt-1">
                Maximális API kérések száma óránként felhasználónként
              </p>
            </div>

            <div className="space-y-2">
              <p className="text-white font-medium">API Kulcs</p>
              <div className="flex flex-col sm:flex-row gap-2">
                <input
                  type="text"
                  value="sk_live_••••••••••••••••••••••••••••"
                  className="input-dark flex-1"
                  readOnly
                />
                <div className="flex gap-2">
                  <button className="btn-secondary flex-1 sm:flex-none whitespace-nowrap">
                    📋 Másolás
                  </button>
                  <button className="btn-primary flex-1 sm:flex-none whitespace-nowrap">
                    🔄 Újragenerálás
                  </button>
                </div>
              </div>
            </div>

            <div className="p-4 bg-blue-900/20 border border-blue-500/50 rounded-lg">
              <p className="text-blue-400 font-medium mb-2">ℹ️ API Dokumentáció</p>
              <p className="text-[#B8B8C3] text-sm mb-3">
                Teljes API dokumentáció elérhető a fejlesztők számára.
              </p>
              <button className="text-[#00AFF2] hover:text-[#2bc48a] text-sm font-medium">
                📖 Dokumentáció Megnyitása →
              </button>
            </div>
          </div>

          <div className="mt-6">
            <button className="btn-primary">
              💾 Változtatások Mentése
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Settings;
